import i18next, { type i18n, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import { SOURCE_LOCALE } from "./locales";

/**
 * react-i18next plumbing (Story 4.2, AC1) — one fixed i18next instance per
 * locale, memoised in a module-level Map.
 *
 * SSG-safety invariant (NFR6 / G3, see i18n-architecture §2.2): prerender renders
 * ~60 (eventually ~600) pages in ONE Node process. A global i18next singleton +
 * `changeLanguage()` would leak the active language across renders. Instead each
 * locale gets its own instance whose `language` is FIXED at init and NEVER
 * mutated — `instanceFor()` is the only constructor and `changeLanguage()` is
 * called nowhere in the codebase. The instance is bound to each page via
 * `<I18nextProvider>` in `LocaleLayout`, so a page always renders against the
 * instance for its own path segment (proven by the AC6 canary).
 */

/**
 * Translation namespaces. Story 4.2 shipped only `common`; Story 4.7 grows the
 * list to one namespace per page/section + `common` for the shell (§4.2).
 * `seo` holds the `PAGE_META` title/description copy (§6.1); `legal` isolates
 * the law-2027 / CIAO@work / GDPR copy that needs native legal review (§7.3).
 */
export const NAMESPACES = [
  "common",
  "home",
  "features",
  "projectManagement",
  "pricing",
  "industries",
  "resources",
  "trainings",
  "gettingStarted",
  "blog",
  "faq",
  "about",
  "contact",
  "freeTrial",
  "seo",
  "legal",
] as const;

/**
 * SSG render fix (Story 4.7, gate AC3/AC6) — catalogs are bundled SYNCHRONOUSLY.
 *
 * Story 4.2 loaded catalogs through `resourcesToBackend` + async `import()`. That
 * is correct for a client SPA, but the build is `ssr:false` + `prerender()`: each
 * of the ~60 routes is rendered with a SYNCHRONOUS `renderToString` in one Node
 * process, and `react.useSuspense:false` means a render NEVER awaits an unresolved
 * namespace — it returns the fallback/key instead. With the async backend the
 * prerendered HTML would therefore contain blank/English-key text for every
 * not-yet-resolved namespace = exactly the visual regression AC3 forbids (the 4.2
 * `_localeCanary` only proved the *language tag*, never a translated string).
 *
 * Fix: `import.meta.glob(..., { eager: true })` makes Vite inline every catalog
 * into the bundle at build time, so the full `resources` tree is present in-tick
 * and `renderToString` emits real copy on the first render. This is a deliberate
 * deviation from i18n-architecture §4.2's per-page code-split: the no-visual-
 * regression invariant outranks the bundle-size optimisation, and at 2 live
 * locales × flat micro-copy the cost is negligible. Revisit (back to a lazy
 * backend with an explicit `loadNamespaces()` preload in the prerender path) only
 * if the bundle grows materially as live locales scale toward 12.
 */
const modules = import.meta.glob<{ default: Record<string, string> }>(
  "./locales/*/*.json",
  { eager: true },
);

/**
 * CMS overlay (DIT-264 / S8 → CMS render). Build-time hydrated catalogs under
 * `.cms/locales/<code>/<ns>.json`, written by `scripts/fetch-cms-content.mjs`
 * from the CMS read API. Merged OVER the baked catalogs so the CMS is the source
 * of truth; when absent (offline dev / CI without `CMS_API_BASE_URL`) the glob is
 * empty and the baked JSON is used as the fallback. Keep this glob path in sync
 * with the fetch script's output dir.
 */
const cmsModules = import.meta.glob<{ default: Record<string, string> }>(
  "./.cms/locales/*/*.json",
  { eager: true },
);

/** `{ [locale]: { [namespace]: catalog } }`, built once: baked, then CMS overlay. */
const RESOURCES: Resource = (() => {
  const out: Resource = {};
  for (const path in modules) {
    // path === "./locales/<locale>/<namespace>.json"
    const m = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
    if (!m) continue;
    const [, lng, ns] = m;
    (out[lng] ??= {})[ns] = modules[path].default;
  }
  // Overlay the CMS catalogs key-by-key (CMS wins; baked fills any gap).
  for (const path in cmsModules) {
    const m = path.match(/\.cms\/locales\/([^/]+)\/([^/]+)\.json$/);
    if (!m) continue;
    const [, lng, ns] = m;
    const baked = ((out[lng] ??= {})[ns] ?? {}) as Record<string, string>;
    out[lng][ns] = { ...baked, ...cmsModules[path].default };
  }
  return out;
})();

const cache = new Map<string, i18n>();

/**
 * Returns the single, memoised i18next instance for `code`, creating + init-ing
 * it on first request. Resources are passed inline (bundled, see above), so the
 * instance is fully populated synchronously — no backend, no async load, no
 * suspense. The fallback locale (`SOURCE_LOCALE`) is always included so a missing
 * key in `code` resolves to its English string, never a blank (AC6).
 *
 * `react.useSuspense:false` + `initImmediate:false` keep init synchronous so the
 * very first prerendered page of the process renders correct, fully-translated
 * HTML (not just later, already-warmed pages).
 */
export function instanceFor(code: string): i18n {
  const cached = cache.get(code);
  if (cached) return cached;

  const inst = i18next.createInstance();
  // NOTE: never call `inst.changeLanguage()` — language is fixed per instance
  // for the lifetime of the process (NFR6 invariant above).
  inst.use(initReactI18next).init({
    lng: code,
    fallbackLng: SOURCE_LOCALE,
    // Only this locale + the fallback are handed to the instance — keeps each
    // instance scoped to its own path segment (no cross-locale string bleed, G3)
    // while resources stay synchronously available.
    resources: {
      [code]: RESOURCES[code] ?? {},
      [SOURCE_LOCALE]: RESOURCES[SOURCE_LOCALE] ?? {},
    },
    ns: [...NAMESPACES],
    defaultNS: "common",
    // Region-suffixed codes (e.g. "fr-be") must resolve against the resource key
    // of the SAME casing. i18next otherwise reformats "fr-be" → "fr-BE" when it
    // resolves, missing the lower-cased "fr-be" bundle (keyed by directory name)
    // and silently falling back to English — see Story 4.10. Our registry codes
    // and locale directories are all lower-case, so pin i18next to that casing.
    // (`<html lang>` uses the registry's `htmlLang` "fr-BE" separately, unaffected.)
    lowerCaseLng: true,
    // Flat semantic keys (§4.2): `t("home:hero.title")` looks up the LITERAL key
    // "hero.title" — the dot is part of the key, not a nesting path. Must match
    // i18next-parser.config.js `keySeparator: false`.
    keySeparator: false,
    nsSeparator: ":",
    supportedLngs: false, // accept the fixed `lng`; no detection/negotiation
    interpolation: { escapeValue: false }, // React already escapes
    initAsync: false, // synchronous init — language + resources ready in-tick
    // (i18next ≥23 renamed `initImmediate: false` → `initAsync: false`)
    react: { useSuspense: false },
  });

  cache.set(code, inst);
  return inst;
}
